import Chapter from "./Chapter";
import { motion } from "framer-motion";
import { Footprints, CircleDot, Check, Download } from "lucide-react";

const ROWS = [
  { label: "Capacité de charge max.", tracked: "≥ 3 100 kg", wheeled: "≥ 3 000 kg" },
  { label: "Hauteur de levage", tracked: "Min. ≤ 130 mm · Max. ≥ 450 mm", wheeled: "Min. ≤ 140 mm · Max. ≥ 400 mm" },
  { label: "Plateau", tracked: "Rotation 360° (unité & plateau)", wheeled: "Plateau à libération rapide" },
  { label: "Vitesse", tracked: "Multi-vitesses réglable", wheeled: "Multi-vitesses réglable" },
  { label: "Franchissement (pente)", tracked: "9°", wheeled: "9°" },
  { label: "Indice de protection", tracked: "IP55", wheeled: "IP55" },
  { label: "Batterie", tracked: "48 V", wheeled: "48 V (libération rapide)" },
  { label: "Dimensions (L × l)", tracked: "135 × 114 cm", wheeled: "135 × 114 cm" },
  { label: "Hauteur repliée", tracked: "13 cm", wheeled: "13 cm" },
  { label: "Poids total", tracked: "240 kg", wheeled: "240 kg" },
];

const VARIANTS = [
  { key: "tracked", name: "Version à chenilles", icon: Footprints, sub: "Tracked Vehicle Mover", featured: true },
];

export default function Specs() {
  return (
    <section id="specs" data-testid="specs-section" className="py-24 lg:py-32 bg-white border-t border-zinc-200">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <Chapter index={5} label="Caractéristiques techniques" />
          <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Une fiche technique taillée pour le terrain.
          </h2>
          <p className="mt-4 text-lg text-[#52525B]">
            Robuste, compacte et autonome, la plateforme à chenilles Sapeur-360 est pensée pour les
            conditions réelles d'intervention et d'atelier.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 max-w-2xl gap-6">
          {VARIANTS.map((v, idx) => (
            <motion.div
              key={v.key}
              data-testid={`spec-variant-${v.key}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className={`bg-white border rounded-sm overflow-hidden ${
                v.featured ? "border-zinc-300 border-t-2 border-t-[#E63946] shadow-sm" : "border-zinc-200"
              }`}
            >
              <div className="flex items-center justify-between gap-3 p-6 border-b border-zinc-200 bg-[#FAFAFA]">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center bg-[#0A0A0A] text-white rounded-sm">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{v.name}</h3>
                    <span className="font-mono-tech text-[11px] uppercase tracking-wider text-[#A1A1AA]">{v.sub}</span>
                  </div>
                </div>
                {v.featured && (
                  <span className="font-mono-tech text-[10px] uppercase tracking-wider bg-[#E63946] text-white px-2 py-1 rounded-sm shrink-0">
                    Modèle Sapeur-360
                  </span>
                )}
              </div>

              <div className="px-6">
                {ROWS.map((r, i) => {
                  const value = r[v.key];
                  const isWeight = r.label === "Poids total";
                  return (
                    <div
                      key={r.label}
                      data-testid={`spec-${v.key}-${i}`}
                      className={`flex items-baseline justify-between gap-6 py-4 ${
                        i !== ROWS.length - 1 ? "border-b border-zinc-200" : ""
                      } ${isWeight ? "bg-[#FAFAFA] -mx-6 px-6" : ""}`}
                    >
                      <span className={`text-[#52525B] ${isWeight ? "font-semibold text-[#0A0A0A]" : ""}`}>{r.label}</span>
                      <span className={`font-mono-tech text-sm font-semibold text-right ${isWeight ? "text-[#E63946]" : "text-[#0A0A0A]"}`}>
                        {value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 flex items-center gap-2 font-mono-tech text-xs uppercase tracking-wider text-[#A1A1AA]">
          <Check className="h-4 w-4 text-[#E63946]" /> Multi-vitesses · IP55 · Batterie 48 V · Poids total 240 kg
        </p>
        <a
          data-testid="brochure-download"
          href="/docs/fiche-technique-sapeur-360.pdf?v=2"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#E63946] text-white font-bold px-6 py-3 rounded-sm transition-colors duration-300"
        >
          <Download className="h-4 w-4" /> Télécharger la fiche technique (PDF)
        </a>
      </div>
    </section>
  );
}
